import { useState, useCallback } from "react";
// Assumed deep nesting of EnvFiles.js, so services/utils are 5 levels up relative to hooks folder
import { getDocuments, searchKeyword, addkeyword, deletekeyword } from "../../../../../services/CommonService";
import Utils from "../../../../../utils";
import { toast } from "react-toastify";

const useDocuments = (selectedUserId, dataParams) => {
  const [fetchedDocuments, setFetchedDocuments] = useState([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});

  const fetchDocuments = useCallback(
    (query = "", page = dataParams.page, limit = dataParams.limit) => {
      if (!selectedUserId) return;

      setLoading(true);

      const processDocuments = (docs) => {
        return docs.map((doc) => ({
          ...doc,
          sleeve_name: doc.sleeve_name ? String(doc.sleeve_name) : "N/A",
          document_type: doc.document_type ? String(doc.document_type) : "N/A",
          keywords: Array.isArray(doc.keywords)
            ? doc.keywords.flatMap((kw) =>
                typeof kw === "string" ? kw.split(/,\s*/) : [String(kw)]
              )
            : doc.keywords
            ? String(doc.keywords).split(/,\s*/).filter((k) => k)
            : [],
          envelopeId: doc.envelope_id || null,
          fileName: doc.file_name || "",
          hasDetails: doc.hasDetails || false,
        }));
      };

      const handleResponse = (response) => {
        const docs = response?.data?.data || [];
        const processedDocs = processDocuments(docs);
        setFetchedDocuments(processedDocs);
        const total = response?.data?.total || processedDocs.length;
        setTotalDocuments(total);
      };

      const handleError = () => {
        setFetchedDocuments([]);
        setTotalDocuments(0);
      };

      const apiCall = !query.trim()
        ? getDocuments({
            email: Utils.loginUserData()?.email,
            user_id: Number(selectedUserId),
            page,
            limit,
          })
        : searchKeyword({
            keyword: query,
            user_id: Number(selectedUserId),
            page,
            limit,
          });

      apiCall
        .then(handleResponse)
        .catch(handleError)
        .finally(() => setLoading(false));
    },
    [selectedUserId, dataParams.limit, dataParams.page]
  );

  const saveKeyword = async (doc, keywordValue, envelopeId, searchText) => {
    const trimmedKeywordValue = keywordValue.trim();
    if (!trimmedKeywordValue) return false;
  
    if (
      doc.keywords.includes(trimmedKeywordValue) ||
      doc.keywords.length >= 15
    ) {
      setErrorMessages((prev) => ({
        ...prev,
        [doc.file_path]: doc.keywords.includes(trimmedKeywordValue)
          ? "This keyword already exists!"
          : "You can add a maximum of 15 keywords!",
      }));
      return false;
    }
  
    const data = {
      envelope_id: parseFloat(envelopeId),
      file_name: doc.file_name,
      keyword: trimmedKeywordValue,
    };
  
    try {
      await addkeyword(data);
      setFetchedDocuments((prevDocs) =>
        prevDocs.map((d) =>
          d.file_path === doc.file_path
            ? { ...d, keywords: [...d.keywords, trimmedKeywordValue] }
            : d
        )
      );
      setErrorMessages((prev) => ({ ...prev, [doc.file_path]: "" }));
      fetchDocuments(searchText, dataParams.page, dataParams.limit);
      return true;
    } catch (error) {
      setErrorMessages((prev) => ({
        ...prev,
        [doc.file_path]: "Error adding keyword. Please try again.",
      }));
      return false;
    }
  };

  const removeKeyword = async (doc, keyword, searchText) => {
    const data = {
      envelope_id: parseFloat(doc.envelope_id),
      file_name: doc.file_name,
      keyword,
    };
    try {
      await deletekeyword(data);
      setFetchedDocuments((prevDocs) =>
        prevDocs.map((d) =>
          d.file_name === doc.file_name
            ? { ...d, keywords: d.keywords.filter((k) => k !== keyword) }
            : d
        )
      );
      fetchDocuments(searchText, dataParams.page, dataParams.limit);
    } catch (error) {
      toast.error("Failed to remove keyword.");
    }
  };

  return {
    fetchedDocuments,
    setFetchedDocuments,
    totalDocuments,
    loading,
    setLoading,
    fetchDocuments,
    saveKeyword,
    removeKeyword,
    errorMessages,
    setErrorMessages,
  };
};

export default useDocuments;
